import React, { useRef, useState } from "react";
import { CameraIcon } from "@heroicons/react/24/outline";
import { useMutation } from "@apollo/client";
import { ADD_WARDROBE_ITEMS } from "@/graphql/mutations/wardrobe";
import { classNames, UploadFile } from "../utils/functions";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";

interface ActionBarProps {
  // No props needed as we'll handle everything internally
}

export default function ActionBar({}: ActionBarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { token } = useAuth();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Set up the mutation hook
  const [addWardrobeItems] = useMutation(ADD_WARDROBE_ITEMS);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      await uploadToWardrobe([file]);
    }
  };
  
  // Function to handle the upload process
  const uploadToWardrobe = async (files: File[]) => {
    setUploading(true);
    setError(null);
    
    try {
      // First upload the files to get signed URLs
      const urls = await UploadFile(files, token);
      
      // Then use the mutation to add items to wardrobe
      const { data } = await addWardrobeItems({
        variables: { images: urls },
      });
      
      if (data?.createWardrobeItems?.success) {
        // Navigate to wardrobe on success
        navigate("/wardrobe", { state: { refetch: true } });
      } else {
        // Handle errors
        setError(
          data?.createWardrobeItems?.errors?.map((e: { fullMessages: string[] }) => 
            e.fullMessages.join(", ")
          ).join("; ") || "Upload failed."
        );
      }
    } catch (err: any) {
      setError(err.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 sm:hidden z-50">
      {/* Decorative top gradient line */}
      <div className="h-px bg-gradient-to-r from-transparent via-pink-500/30 to-transparent" />

      {/* Glass-morphism background */}
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-gray-200/50 dark:border-gray-700/50 shadow-lg">
        <div className="flex items-center justify-around px-4 py-3">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            className="hidden"
            disabled={uploading}
          />

          {/* Main action button - toggles media chooser */}
          <button
            onClick={triggerFileInput}
            disabled={uploading}
            className={classNames(
              "group relative flex flex-col items-center justify-center w-16 h-16 rounded-full -mt-6",
              "bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500",
              "text-white",
              "hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl",
              uploading ? "opacity-70 cursor-not-allowed" : ""
            )}
          >
            {/* Icon and text are in a z-10 wrapper to ensure they stay visible */}
            <div className="relative z-10 flex flex-col items-center justify-center">
              {uploading ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              ) : (
                <CameraIcon className="h-8 w-8 group-hover:scale-110 transition-transform duration-300" />
              )}
            </div>

            {/* Enhanced gradient overlay - fixed positioning */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Elegant shine effect - fixed positioning and animation */}
            <div className="absolute inset-0 rounded-full overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-full group-hover:-translate-x-full transition-transform duration-700" />
            </div>
          </button>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="absolute bottom-20 left-0 right-0 bg-red-500 text-white p-2 text-center text-sm font-light tracking-wider">
          {error}
        </div>
      )}
      
      {/* Upload status */}
      {uploading && (
        <div className="absolute bottom-20 left-0 right-0 bg-indigo-500/80 backdrop-blur-sm text-white p-2 text-center text-sm font-light tracking-wider">
          Adding to your wardrobe...
        </div>
      )}
    </div>
  );
}
